'use client';

import { useEffect, useState, useSyncExternalStore } from 'react';
import { RemotionRenderer } from '@/components/visualizations/remotion-renderer';
import {
  getVisualizationVersion,
  subscribeVisualizationVersion,
} from '@/lib/visualization-refresh-bus';

interface LiveRemotionRendererProps {
  /** Saved visualization id. When set, the renderer subscribes to edit
   *  events for this id and re-fetches `componentCode` from the
   *  visualization-source API whenever the model edits the saved
   *  source via `editVisualization`. */
  visualizationId: string;
  /** Initial componentCode to render before any refetch. Comes from the
   *  tool result that produced the video. */
  initialComponentCode: string;
  title: string;
  durationInFrames: number;
  fps: number;
  width: number;
  height: number;
  displayContext?: 'inline' | 'artifact';
}

/**
 * RemotionRenderer wrapper that auto-refreshes when the saved
 * visualization is edited via the `editVisualization` tool.
 *
 * Why this exists: the model can patch a narrated video's componentCode
 * surgically (find + replace via `editVisualization`) — but the rendered
 * video card was a static prop snapshot of the code at tool-completion.
 * Without this wrapper, the model would edit and the user would see no
 * change until they reloaded the page.
 *
 * Mechanism: the lab chat watches messages for `editVisualization` tool
 * completions and calls `bumpVisualizationVersion(id)` on the refresh
 * bus. This wrapper subscribes to that id; when the version increments,
 * we GET /api/visualizations/[id]/source?fields=componentCode and pass
 * the new code down to RemotionRenderer, which already recompiles on
 * componentCode change.
 */
export function LiveRemotionRenderer({
  visualizationId,
  initialComponentCode,
  title,
  durationInFrames,
  fps,
  width,
  height,
  displayContext = 'inline',
}: LiveRemotionRendererProps) {
  const [componentCode, setComponentCode] = useState(initialComponentCode);
  const [refreshing, setRefreshing] = useState(false);

  // useSyncExternalStore reads the version counter for THIS visualization
  // from the bus. Bumps trigger a re-render of this component without
  // disturbing siblings (each visualizationId is its own subscription).
  const version = useSyncExternalStore(
    (notify) => subscribeVisualizationVersion(visualizationId, notify),
    () => getVisualizationVersion(visualizationId),
    () => 0,
  );

  // On version bump, refetch the latest componentCode. The first render
  // (version=0) uses the initial prop and skips the fetch — saves a
  // round-trip on the common case of "video just rendered, never edited."
  useEffect(() => {
    if (version === 0) return;
    let cancelled = false;
    setRefreshing(true);
    fetch(`/api/visualizations/${visualizationId}/source?fields=componentCode`)
      .then(async (r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json() as Promise<{ componentCode?: string }>;
      })
      .then((data) => {
        if (cancelled) return;
        if (typeof data.componentCode === 'string' && data.componentCode.length > 0) {
          setComponentCode(data.componentCode);
        }
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('[LiveRemotionRenderer] refresh failed:', err);
      })
      .finally(() => {
        if (cancelled) return;
        setRefreshing(false);
      });
    return () => {
      cancelled = true;
    };
  }, [version, visualizationId]);

  return (
    <div className="relative">
      <RemotionRenderer
        title={title}
        componentCode={componentCode}
        durationInFrames={durationInFrames}
        fps={fps}
        width={width}
        height={height}
        visualizationId={visualizationId}
        displayContext={displayContext}
      />
      {refreshing ? (
        <div className="absolute right-2 top-2 inline-flex items-center gap-1.5 rounded-full bg-violet-500/[0.08] border border-violet-400/25 px-2.5 py-1 text-[10px] font-medium text-violet-200">
          <span className="size-1.5 rounded-full bg-violet-400 animate-pulse" />
          Applying edit…
        </div>
      ) : null}
    </div>
  );
}
